import { useQuery } from "@tanstack/react-query";
import { fetchTrashCount } from "@/lib/api/handlers/GuestTrashHandling";
import { useGuestTrashFeature } from "@/lib/guests/guestTrashFeature";
import { guestTrashKey } from "@/lib/guests/guestTrashInvalidate";

/**
 * Whether the Papierkorb entry has a reason to exist for this viewer, in
 * this order (audit-B lens-6 H1):
 *   1. `/trash/count` says the trash is ON for the event (`enabled: true`) —
 *      a fresh session on an empty trash finds the page (GTU-3, help tip);
 *   2. it holds rows (`count > 0`);
 *   3. this browser LEARNED the flag (a delete impact or a TRASH-mode delete)
 *      — the only rule an older backend without `enabled` can offer.
 * A backend without the route answers 404 → `null` → nothing learned →
 * hidden. Fails closed.
 */
export function useGuestTrashVisible(eventId: string | undefined, allowed: boolean): boolean {
  const known = useGuestTrashFeature((s) => (eventId ? s.enabledByEvent[eventId] === true : false));
  const q = useQuery({
    queryKey: [...guestTrashKey(eventId ?? ""), "count"],
    queryFn: () => fetchTrashCount(eventId ?? ""),
    enabled: allowed && !!eventId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const answer = q.data ?? null;
  const enabled = answer?.enabled === true;
  const hasRows = typeof answer?.count === "number" && answer.count > 0;
  return allowed && (enabled || hasRows || known);
}
